/***
 * Created on 2023/9/6
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from "react-i18next";
import {useTogglePopup} from "../../hook/hooks";
import {
    getCurrentDomainRegion,
    SUPPORTED_LANGUAGES,
    GLOBAL,
    CHINA,
    getLocalLanguage,
    detectDefaultLanguage, setLocalLanguage
} from "../../service/common";
import {useBoundedStore} from '../../lib/store';
import RegionSwitchWarning from "../dialog/region_switch_warning";
import Cookie from 'js-cookie';
import ChinaRegionLangWarningDialog from "./china_region_lang_warning_dialog";
import region_global_svg from "../../static/img/header/ic-region-global.svg";
import region_china_svg from "../../static/img/header/ic-region-china.svg";
import attention_svg from "../../static/img/utils/ic-sys-attention.svg";

function SettingsHeader(props) {
    const { t, i18n } = useTranslation();
    const {isShow, toggleAction, elemRef, setIsShow} = useTogglePopup({pinContent: true});
    const [subMenu, setSubMenu] = useState('close');
    const store = useBoundedStore();
    const currentRegion = getCurrentDomainRegion();
    const [switchToRegion, setSwitchToRegion] = useState(null);
    const [switchWarning, setSwitchWarning] = useState(false);
    const [chinaLangRegion, setChinaLangRegion] = useState(false);
    const currentLangRef = useRef(null);
    const [regionNotMatchWarning, setRegionNotMatchWarning] = useState( !Cookie.get('regionNotMatchWarning') ? Cookie.get('client_region') !== currentRegion : false);

    const initCurrentLang = () => {
        let lang = getLocalLanguage();
        if (!lang) {
            lang = detectDefaultLanguage();
            setLocalLanguage(lang);
            onLanguageChange(null, lang);
        }
    }

    const onLanguageChange = (e, value) => {
        if (e) {
            e.target.classList.add('show-spinner');
        }
        const url = new URL(location.href);
        if (url.searchParams.get('lang')) {
            url.searchParams.delete('lang');
            window.history.replaceState(null, '', `${url.pathname}?${url.searchParams.toString()}`);
        }
        if (currentRegion === CHINA) {
            currentLangRef.current = value;
            setChinaLangRegion(true);
        }
        else {
            i18n.changeLanguage(value, ()=>{
                if (e) {
                    e.target.classList.remove('show-spinner');
                }
            });
            store.setLanguage(value);
            setLocalLanguage(value);
        }
    }
    initCurrentLang();

    const switchRegion = (region, lang) => {
        if (currentRegion === region) return;

        if (region === CHINA) {
            // window.location.href = window.acc_mf.common.APP_ACC_URL_CN;
            window.location.host = window.acc_mf.common.APP_ACC_URL_CN.replace(/https?:\/\//i, '');
        }
        else {
            const url = new URL(location.href)
            url.host = window.acc_mf.common.APP_ACC_URL.replace(/https?:\/\//i, '');
            if (lang) {
                if (url.searchParams.get('lang')) {
                    url.searchParams.set('lang', lang);
                }
            }
            return location.href = url.toString();
            // window.location.host = window.acc_mf.common.APP_ACC_URL.replace(/https?:\/\//i, '');
        }
        closeRegionSwitchWarning()
    }

    const forceGlobal = () => {
        setChinaLangRegion(false);
        switchRegion(GLOBAL, currentLangRef?.current);
    }

    const onClickSetRegion = (region) => {
        setSwitchToRegion(region);
        if (!Cookie.get('noRegionSwitchWarning')) {
            setSwitchWarning(true);
        }
        else {
            switchRegion(region);
        }
        setIsShow(false);
    }

    const onClickCookieManger = (region) => {
        setIsShow(false);
    }

    const closeRegionSwitchWarning = () => {
        Cookie.set('noRegionSwitchWarning', true, {expires: 1, secure: true, sameSite: 'LAX'});
        setSwitchWarning(false);
    }

    const closeRegionMismatchWarning = () => {
        Cookie.set('regionNotMatchWarning', true, {expires: 1, secure: true, sameSite: 'LAX'});
        setRegionNotMatchWarning(false);
    }

    const onClickPreferredRegion = () => {
        // setRegionNotMatchWarning(false);
        if (currentRegion === CHINA) {
            window.location.host = window.acc_mf.common.APP_ACC_URL_CN.replace(/https?:\/\//i, '');
        }
        else {
            window.location.host = window.acc_mf.common.APP_ACC_URL.replace(/https?:\/\//i, '');
        }
        closeRegionMismatchWarning()
    }

    useEffect(() => {
        const appHead = document.querySelector('.app-head');
        if (!isShow) {
            setSubMenu('close');
            appHead.removeAttribute('data-z-index');
        }
        else {
            appHead.setAttribute('data-z-index', 'top');
        }
    }, [isShow])

    return (
        <div className={`util-btn ${isShow ? 'active' : ''}`} ref={elemRef} onClick={toggleAction} util-type="setting" data-testid="settings-header">
            <div className="comp-dropdown hide">
                <div className="multi-level-dropdown" data-secondary-menu={['language', 'region'].includes(subMenu) ? 'open' : 'close'}>
                    <div className="multi-level-dropdown__panel" data-menu-level="1">
                        <ul className="dropdown-menu">
                            <li className="dropdown-menu__head">{t('@Language@')}</li>
                            <li className="dropdown-menu__item cursor-pointer" onClick={() => setSubMenu('language')}>
                                <div className="dropdown-menu__item-group">
                                    <div className="dropdown-menu__item-group__label">
                                        {SUPPORTED_LANGUAGES.find(lang => lang.value === i18n.language)?.name}
                                    </div>
                                    <div className="dropdown-menu__item-group__arrow -!mr-8"></div>
                                </div>
                            </li>
                            <li className="dropdown-menu__divider !mt-10 !mb-10"></li>
                            <li className="dropdown-menu__head">{t('@Region@')}</li>
                            <li className="dropdown-menu__item cursor-pointer" onClick={() => setSubMenu('region')}
                                data-option="region">
                                <div className="dropdown-menu__item-group">
                                    <div className="w-18px mr-8"><img src={currentRegion===GLOBAL ? region_global_svg : region_china_svg} alt={currentRegion}/>
                                    </div>
                                    <div className="dropdown-menu__item-group__label">{currentRegion === GLOBAL ? t('@Global@') : t('@China@')}</div>
                                    <div className="dropdown-menu__item-group__arrow -!mr-8"></div>
                                </div>
                            </li>
                            {/*<li className="dropdown-menu__divider !mt-10 !mb-10"></li>*/}
                            {/*<li className="dropdown-menu__head">{t('@Management@')}</li>*/}
                            {/*<li className="dropdown-menu__item cursor-pointer" onClick={onClickCookieManger}>{t('@Cookies@')}</li>*/}
                        </ul>
                    </div>

                    <div className="multi-level-dropdown__panel" data-menu-level="2">
                        <ul className="dropdown-menu">
                            <li className="dropdown-menu__navbar">
                                <div className="dropdown-menu__navbar__back cursor-pointer" onClick={() => setSubMenu('close')}>
                                    <div className="dropdown-menu__navbar__arrow"></div>
                                    <div className="dropdown-menu__navbar__label">{t('@Back@')}</div>
                                </div>
                            </li>
                            <li className="dropdown-menu__scroll-content">
                                <ul className={`display-block ${subMenu==='language' ? '' : '!hide'}`} data-option-ta="language">
                                    {
                                        SUPPORTED_LANGUAGES.map(lang =>
                                            <li className={`dropdown-menu__item cursor-pointer ${lang.value === i18n.language ? 'active marked' : ''}`}
                                                onClick={(e) => onLanguageChange(e, lang.value)}
                                                key={lang.value}>{lang.name}</li>)
                                    }
                                </ul>

                                <ul className={`display-block ${subMenu==='region' ? '' : '!hide'}`} data-option-ta="region">
                                    <li className={`dropdown-menu__item cursor-pointer ${currentRegion===GLOBAL ? 'active marked' : ''}`} onClick={() => onClickSetRegion(GLOBAL)}>{t('@Global@')}</li>
                                    <li className={`dropdown-menu__item cursor-pointer ${currentRegion===CHINA ? 'active marked' : ''}`} onClick={() => onClickSetRegion(CHINA)}>{t('@China@')}</li>
                                </ul>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {switchWarning && <RegionSwitchWarning showModal={switchWarning} closeModal={closeRegionSwitchWarning}
                                                   mode="switch_region" submitAction={switchRegion}
                                                   switchTo={switchToRegion}/>}
            {regionNotMatchWarning &&
                <RegionSwitchWarning showModal={regionNotMatchWarning} closeModal={closeRegionMismatchWarning}
                                     submitAction={onClickPreferredRegion} mode="region_mismatch"/>}
            {chinaLangRegion &&
                <ChinaRegionLangWarningDialog showModal={chinaLangRegion} closeModal={() => setChinaLangRegion(false)}
                                              submitAction={() => forceGlobal()}/>}
        </div>
);
}

export default SettingsHeader;